import {
  Box,
  Checkbox,
  Fade,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid2 as Grid,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import Modal from "@mui/material/Modal";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useGet from "../../../../services/hooks/useGet";
import usePost from "../../../../services/hooks/usePost";
import { Button, Heading, Input } from "../../../../shared/ReusableComponents";
import CloseBtn from "../../../../shared/ReusableComponents/CloseBtn";
import { times } from "../../../../shared/constants/all-constant";
import { apiEndpoints } from "../../../../shared/constants/api-constant";
import { AlertType } from "../../../../shared/constants/enums";
import { CommonFunctions } from "../../../../shared/utils/CommonFunctions";
import {
  DateFormatter,
  DateTimeFormats,
} from "../../../../shared/utils/DateFormatter";
import { toast } from "../../../../store/toastSlice";
import { cssVar } from "../../../../theme/theme";
import MedicationAlert from "./MedicationAlert";
import { IMedicationLogger, IMedicineType, IReason } from "./types";
import moment from "moment";
import { DateTimeInput } from "../../../../shared/ReusableComponents/DateTimeInput";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50%",
  maxWidth: "50%",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  px: "4.375rem",
  py: "1.875rem",
  backgroundColor: "#F5F5F5",
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    margin: "0px 0px",
    padding: "0px 0px",
    // height: "45px",
    "&.Mui-focused fieldset": {
      borderColor: "#000000",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#000000",
      borderWidth: "1px",
    },
    "&.Mui-disabled": {
      backgroundColor: "#F5F5F5",
      "& fieldset": {
        borderColor: "#B1B1B1",
      },
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#000000",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#000000",
    borderWidth: "1px",
  },
  "& .MuiTableCell-root": {
    padding: "10px",
  },
};

export const MedicationLogger = (props: IMedicationLogger) => {
  const {
    isOpen,
    setIsOpen,
    selectedMedication,
    showMedicationAlert,
    selectedFrequency,
    setShowMedicationAlert,
    loadData,
    loadHistory,
  } = props;
  const { data, getService } = useGet();
  const { postService } = usePost();
  const auth = useSelector((state: any) => state.login);
  const [showInputs, setShowInputs] =
    useState<React.SetStateAction<boolean>>(true);
  const [dose, setDose] = useState("");
  const [doseError, setDoseError] = useState<boolean>(false);
  const [counterSignedBy, setCounterSignedBy] = useState("");
  const [counterSignedByError, setCounterSignedByError] =
    useState<boolean>(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<boolean>(false);
  const [reasons, setReasons] = useState<IReason[] | null>(null);
  const [vteAssessed, setVteAssessed] = useState(
    selectedMedication.vteAssessed
  );
  const [prophylaxisNotRequired, setProphylaxisNotRequired] = useState(
    selectedMedication.prophylaxis
  );
  const [contraindicated, setContraindicated] = useState(
    selectedMedication.contraindicated
  );
  const [administerTime, setAdministerTime] = useState(moment());

  const signedBy = CommonFunctions.formatFirstAndLastName(
    auth.userInfo?.userFirstName,
    auth.userInfo?.userLastName,
    { suffixKey: "sn" }
  );

  const dispatch = useDispatch();
  useEffect(() => {
    if (!data && !showInputs) {
      getService(`${apiEndpoints.REASONS}`).then(() => {});
    }
  }, [showInputs, getService, data]);

  useEffect(() => {
    if (data) {
      setReasons(data);
    } else {
      setReasons(null);
    }
  }, [data]);

  const tableHeaders = [
    { label: "Medicine", key: "medicineName" },
    { label: "Route", key: "route" },
    { label: "Dose", key: "dose" },
  ];

  if (selectedMedication.medicineType === IMedicineType.PRN) {
    tableHeaders.push({ label: "Max PRN (dose/24 hrs)", key: "maxPRN" });
  }
  tableHeaders.push({ label: "Indication", key: "indication" });

  const handleDoseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setDose(value);
    if (value !== "") {
      setDoseError(false);
    }
    // const mgRegex = /^(\d+(\.\d+)?)\s*mg$/;
    // if (value === "") {
    //   setDoseError(false);
    //   return;
    // }
    // const match = value.match(mgRegex);
    // if (match) {
    //   const changeToFloat = parseFloat(match[1]);
    //   if (changeToFloat >= 4 && changeToFloat <= 8) {
    //     setDoseError(false);
    //   } else {
    //     setDoseError(true);
    //   }
    // } else {
    //   setDoseError(true);
    // }
  };

  const handleCounterSignedByChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCounterSignedBy(e.target.value);
    setCounterSignedByError(false);
  };

  const handleReasonChange = (e: any) => {
    const selectedReason = e.target.value;
    const trimmedCounterSignedBy = counterSignedBy.trim();
    setReason(selectedReason);
    if (selectedReason) {
      setReasonError(false);
      setCounterSignedByError(false);
    }
    if (trimmedCounterSignedBy !== "") {
      setCounterSignedByError(false);
    }
  };

  const handleNotGivenClick = () => {
    setShowInputs(false);
    setCounterSignedBy("");
    setCounterSignedByError(false);
  };

  const resetFields = () => {
    setShowInputs(true);
    setDose("");
    setDoseError(false);
    setCounterSignedBy("");
    setCounterSignedByError(false);
    setReason("");
    setReasons(null);
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    const trimmedDose = dose.trim();
    const trimmedReason = reason.trim();
    const trimmedCounterSignedBy = counterSignedBy.trim();

    let hasError = false;

    if (showInputs) {
      if (trimmedDose === "") {
        setDoseError(true);
        hasError = true;
      } else {
        setDoseError(false);
      }

      if (trimmedCounterSignedBy === "") {
        setCounterSignedByError(true);
        hasError = true;
      } else {
        setCounterSignedByError(false);
      }
    }
    if (!showInputs && !trimmedReason) {
      setReasonError(true);
      hasError = true;
    } else {
      setReasonError(false);
    }

    if (trimmedCounterSignedBy === "") {
      setCounterSignedByError(true);
      hasError = true;
    } else {
      setCounterSignedByError(false);
    }

    if (hasError) return;
    const newData = {
      scheduleTimeId: selectedFrequency.doseDetails.id,
      // scheduleTime: selectedFrequency.doseDetails.scheduleTime,
      scheduleTime: administerTime.format("HH:mm"),
      prescribedId: selectedFrequency.doseDetails.prescribedId,
      isGiven: showInputs,
      dose: trimmedDose,
      reason: trimmedReason,
      signedBy: signedBy,
      counterSignedBy: trimmedCounterSignedBy,
      updatedOn: new Date().toISOString(),
      ...(showInputs && {
        vteAssessed,
        prophylaxis: prophylaxisNotRequired,
        contraindicated,
      }),
    };

    postService(apiEndpoints.MEDICATIONS, newData)
      .then(() => {
        dispatch(
          toast({
            isOpen: true,
            message: showInputs
              ? `${selectedMedication.medicineName} ${selectedMedication.medicineType} Medicine has been Successfully Administered.`
              : `${selectedMedication.medicineName} ${selectedMedication.medicineType} Medicine has not been Administered.`,
            type: showInputs ? AlertType.SUCCESS : AlertType.WARNING,
          })
        );
        setIsOpen(false);
      })
      .then(() => loadHistory());
  };

  // const getMedicineDisplayTime = (schedule: string) => {
  //   return moment(times.find((x) => x.value === schedule)?.key, "HHmm").format(
  //     "hh:mm A"
  //   );
  // };
  const timeToDisplay = times.find(
    (x) => x.value === selectedFrequency.schedule
  )?.key;

  const isVariableType =
    selectedMedication?.medicineType === "Variable Dose" ||
    selectedMedication?.medicineType === IMedicineType.Variable;
  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={isOpen}
        onClose={resetFields}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <span className="grey-background">
          {showMedicationAlert ? (
            <MedicationAlert
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              setShowMedicationAlert={setShowMedicationAlert}
              selectedFrequency={selectedFrequency}
            />
          ) : (
            <Fade in={isOpen}>
              <Box sx={style}>
                <CloseBtn onClose={resetFields} />
                <Box sx={{ textAlign: "center" }}>
                  <span
                    className="material-symbols-outlined medicine-icon"
                    style={{ fontSize: "50px" }}
                  >
                    vaccines
                  </span>
                </Box>

                <div style={{ overflowX: "auto", marginTop: 2 }}>
                  <Table
                    sx={{
                      mt: 2,
                      "& .MuiTableCell-root": {
                        fontSize: "0.875rem",
                      },
                    }}
                  >
                    <TableHead
                      sx={{
                        backgroundColor: "var(--color-default)",
                        border: "2px solid #F5F5F5",
                      }}
                    >
                      <TableRow>
                        {tableHeaders.map((header) => (
                          <TableCell key={header.key}>
                            <strong>{header.label}</strong>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        sx={{
                          backgroundColor: "#fff",
                        }}
                        className="tabledata"
                      >
                        <TableCell>{selectedMedication.medicineName}</TableCell>
                        <TableCell>{selectedMedication.route}</TableCell>
                        <TableCell>{`${selectedMedication.dose}-${selectedMedication.frequencyName}`}</TableCell>
                        {selectedMedication.medicineType ===
                          IMedicineType.PRN && (
                          <TableCell>{selectedMedication.maxPRN}</TableCell>
                        )}
                        <TableCell>{selectedMedication.indication}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                {showInputs && (
                  <FormGroup
                    sx={{
                      marginLeft: "-10px",
                      marginRight: "-10px",
                      width: "100%",
                      flexWrap: "nowrap",
                      overflowX: "auto",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Heading
                          text="VTE risk assessed:"
                          font="1.25rem"
                          style={{ marginRight: "20px", whiteSpace: "nowrap" }}
                        />
                        <FormControlLabel
                          className="padding-right-10 "
                          control={
                            <Checkbox
                              checked={vteAssessed ?? false}
                              onChange={(e) => setVteAssessed(e.target.checked)}
                            />
                          }
                          label={<Heading text="Yes" font="1.25rem" />}
                        />
                      </div>
                      <FormControlLabel
                        className="padding-right-10"
                        control={
                          <Checkbox
                            checked={prophylaxisNotRequired ?? false}
                            onChange={(e) =>
                              setProphylaxisNotRequired(e.target.checked)
                            }
                          />
                        }
                        label={
                          <Heading
                            text=" Prophylaxis not required"
                            font="1.25rem"
                            style={{ whiteSpace: "nowrap" }}
                          />
                        }
                      />
                      <FormControlLabel
                        className="padding-left-10"
                        control={
                          <Checkbox
                            checked={contraindicated ?? false}
                            onChange={(e) =>
                              setContraindicated(e.target.checked)
                            }
                          />
                        }
                        label={
                          <Heading text="Contraindicated" font="1.25rem" />
                        }
                      />
                    </Box>
                  </FormGroup>
                )}
                {showInputs && (
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid size={3}>
                        <Input
                          label="Date"
                          fullWidth
                          name="date"
                          value={DateFormatter.formatDate(
                            selectedMedication.dateTime
                          )}
                          type="text"
                          InputLabelProps={{
                            shrink: true,
                            style: { color: "var(--color-border-model)" },
                          }}
                          disabled
                        />
                      </Grid>
                      <Grid size={3}>
                        <DateTimeInput
                          label="Time"
                          name="time"
                          type="time"
                          size="small"
                          format={DateTimeFormats.TimeFormat}
                          value={administerTime}
                          classes={"administer-date-picker"}
                          timeSteps={{ minutes: 1 }}
                          onChange={(val) => {
                            setAdministerTime(val);
                          }}
                        />
                      </Grid>
                      <Grid size={6}>
                        <Input
                          label="Dose"
                          fullWidth
                          required
                          name="dose"
                          type="text"
                          value={dose}
                          onChange={handleDoseChange}
                          hasError={doseError ? "Dose is required" : ""}
                          error={!!doseError}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  {showInputs ? (
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Input
                          label="Signed by (SN)"
                          fullWidth
                          disabled
                          name="signedBy"
                          value={signedBy}
                          InputLabelProps={{
                            shrink: true,
                            style: { color: "var(--color-border-model)" },
                          }}
                        />
                      </Grid>
                      <Grid size={6}>
                        <Input
                          label="Counter Signed By (RN)"
                          name="counterSignedBy"
                          type="text"
                          required
                          value={counterSignedBy}
                          onChange={handleCounterSignedByChange}
                          error={!!counterSignedByError}
                          hasError={
                            counterSignedByError
                              ? "Counter Signed By (RN) is required"
                              : ""
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                    >
                      <Grid>
                        <FormControl fullWidth error={reasonError}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ marginRight: "10px" }}
                            >
                              Reasons *:
                            </Typography>
                            <Select
                              value={reason}
                              onChange={handleReasonChange}
                              size="small"
                              displayEmpty
                              sx={{
                                color: reasonError
                                  ? "var(--color-error-main)"
                                  : "primary.main",
                                width: "391px",
                                height: "40px",
                                marginLeft: "10px",
                                border: reasonError
                                  ? "1px solid var(--color-error-main)"
                                  : "none",
                                backgroundColor: "#FFFFFF",
                                borderColor: "transparent",
                                "& fieldset": {
                                  border: reasonError
                                    ? "1px solid var(--color-error-main)"
                                    : "none",
                                },
                                "&:hover fieldset": {
                                  border: reasonError
                                    ? "1px solid var(--color-error-main)"
                                    : "none",
                                },
                                "& .MuiSelect-icon": {
                                  color: reasonError ? "red" : "primary.main",
                                  transform: "rotate(0deg)",
                                },
                                "& .MuiSelect-root.Mui-focused .MuiSelect-icon":
                                  {
                                    transform: "rotate(0deg)",
                                  },
                                "& .MuiSelect-root.Mui-disabled": {
                                  backgroundColor: "#F5F5F5",
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 300,
                                    overflowY: "auto",
                                  },
                                },
                              }}
                            >
                              <MenuItem value="" disabled>
                                Select
                              </MenuItem>
                              {!showInputs &&
                                reasons &&
                                Array.isArray(reasons) &&
                                reasons?.map((item: IReason) => {
                                  return (
                                    <MenuItem value={item.reason} key={item.id}>
                                      {item.reason}
                                    </MenuItem>
                                  );
                                })}
                            </Select>
                          </div>
                          {reasonError && (
                            <FormHelperText
                              error
                              sx={{ ml: "17%", fontSize: "10px" }}
                            >
                              Reason is Required
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                </Box>
                {reason && (
                  <Box sx={{ mt: 2 }}>
                    <Grid container justifyContent="center" alignItems="center">
                      <Grid size={4}>
                        <Input
                          label="Counter Signed By (RN)"
                          name="counterSignedBy"
                          required
                          type="text"
                          value={counterSignedBy}
                          onChange={handleCounterSignedByChange}
                          error={!!counterSignedByError}
                          hasError={
                            counterSignedByError
                              ? "Counter Signed By (RN) is required"
                              : ""
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    justifyContent: "center",
                    columnGap: "20px",
                    alignItems: "center",
                  }}
                >
                  {selectedMedication.medicineType === IMedicineType.Regular &&
                    !selectedMedication.isVariable &&
                    showInputs && (
                      <Link
                        sx={{
                          cursor: "pointer",
                          color: "info.main",
                          textDecorationColor: cssVar("--color-info-main"),
                          flexShrink: 0,
                        }}
                        onClick={handleNotGivenClick}
                      >
                        <span>Not Administered</span>
                      </Link>
                    )}
                  <Button
                    text="Cancel"
                    variant="outlined"
                    onClick={() => resetFields()}
                  />
                  <Button text="sign" onClick={handleSubmit} />
                </Box>
              </Box>
            </Fade>
          )}
        </span>
      </Modal>
    </div>
  );
};
